import {
  Component,
  OnInit,
  OnChanges,
  AfterViewInit,
  Input,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { DataSet } from '../models';
import { ChartsVisualization } from '@lfx/core/models';
import { Chart } from 'chart.js';

@Component({
  selector: 'lfx-doughnut-chart',
  templateUrl: './doughnut-chart.component.html',
})
export class DoughnutChartComponent
  implements OnInit, OnChanges, AfterViewInit {
  @Input() chartColors: string[];
  @Input() chartData: any;
  @Input('visualizationData')
  set visualizationData(visualization: ChartsVisualization) {
    this.dataSets = visualization.dataSets;
    this.xAxesLabels = visualization.labels;
  }

  @Input() fill: boolean;
  @Input() extraOptions = {};
  @ViewChild('chartCanvas', { static: false }) canvas: ElementRef;

  chartType = 'doughnut';
  dataSets: DataSet[] = [];
  xAxesLabels: (string | number)[] = [];
  chart: Chart;
  options: any;
  borderWidth = '1';

  /**
   * colors to Use must be HEX to support the hexRoRgb method
   */
  @Input() fillColors = [
    '#D32F2F',
    '536DFE',
    '#FFA000',
    '#4CAF50',
    '#795548',
    '#0288D1',
  ];

  @Input() borderColors = [
    '#D32F2F',
    '536DFE',
    '#FFA000',
    '#4CAF50',
    '#795548',
    '#0288D1',
  ];

  constructor() {}

  ngOnInit() {
    this.createCenterLabelPlugin();
    this.options = {
      responsive: true,
      maintainAspectRatio: false,
      ...this.extraOptions,
    };
  }

  ngOnChanges(): void {
    if (this.chart) {
      this.updateChart();
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.createChart();

      if (this.chartData !== undefined) {
      }
    });
  }

  private createChart() {
    const canvas = this.canvas.nativeElement.getContext('2d');

    this.chart = new Chart(canvas, {
      type: this.chartType,
      data: {
        labels: this.xAxesLabels,
        datasets: this.dataSets.map((dataSet, index) =>
          Object.assign({}, this.createExtraSettingsForDataSet(index), dataSet)
        ),
      },
      options: this.options,
    });
  }

  private updateChart() {
    this.chart.data = {
      labels: this.xAxesLabels,
      datasets: this.dataSets.map((dataSet, index) =>
        Object.assign({}, this.createExtraSettingsForDataSet(index), dataSet)
      ),
    };
    this.chart.update();
  }

  private hexToRgb(hex, opacity = 1) {
    hex = hex || this.addColor();
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

    if (!result) {
      return hex;
    }

    return (
      'rgba( ' +
      parseInt(result[1], 16) +
      ',' +
      parseInt(result[2], 16) +
      ',' +
      parseInt(result[3], 16) +
      ',' +
      opacity +
      ')'
    );
  }

  private generateRandomColor() {
    return '#' + Math.floor(Math.random() * 16777215).toString(16);
  }

  private addColor() {
    const newColor = this.generateRandomColor();

    this.fillColors.push(newColor);
    this.borderColors.push(newColor);

    return newColor;
  }

  private generateBackgrounds(index: number) {
    return this.dataSets[index].data.map((item, i) =>
      this.hexToRgb(this.fillColors[i], 0.6)
    );
  }

  private generateBorders(index: number) {
    return this.dataSets[index].data.map((item, i) => this.borderColors[i]);
  }

  private createExtraSettingsForDataSet(index: number): any {
    return {
      borderColor: this.generateBorders(index),
      backgroundColor: this.generateBackgrounds(index),
      fill: this.fill ? 'origin' : false,
      borderWidth: this.borderWidth,
    };
  }

  private createCenterLabelPlugin() {
    Chart.pluginService.register({
      beforeDraw: chart => {
        const center = chart.config.options.elements.center;

        if (center) {
          this.drawCenterLabel(chart, center.title, 50, 15);
          this.drawCenterLabel(chart, center.subtitle, -20);
        }
      },
    });
  }

  private drawCenterLabel(
    chart: Chart,
    content: any,
    centerOffset = 0,
    fontRatioModifier = 30
  ) {
    const context = chart.chart.ctx;
    const fontStyle = content.fontStyle || 'Sans Serif';
    const textContent = content.text;

    const color = content.color || '#000';
    const sidePadding = content.sidePadding || 20;

    const sidePaddingCalculated = (sidePadding / 100) * (chart.innerRadius * 2);

    context.font = '30px ' + fontStyle;

    const stringWidth = context.measureText(textContent).width;
    const elementWidth = chart.innerRadius * 2 - sidePaddingCalculated;

    const widthRatio = elementWidth / stringWidth;
    const newFontSize = Math.floor(fontRatioModifier * widthRatio);
    const elementHeight = chart.innerRadius * 2;
    const fontSizeToUse = Math.min(newFontSize, elementHeight);

    context.textAlign = 'center';
    context.textBaseline = 'middle';

    const centerX = (chart.chartArea.left + chart.chartArea.right) / 2;
    const positionY =
      (chart.chartArea.top + chart.chartArea.bottom - centerOffset) / 2;

    context.font = fontSizeToUse + 'px ' + fontStyle;
    context.fillStyle = color;

    context.fillText(textContent, centerX, positionY);
  }
}
